/**
 * AC-1517 / AC-1550 (epic AC-1493 P7D / AC-1537) — helper for computing
 * which provider API keys are missing for a given set of workflow steps.
 *
 * Pulled out of `WorkflowEditor.tsx` so the logic is unit-testable without
 * mounting the full editor. The editor wraps this in a `useMemo` that
 * short-circuits when BYOK mode is off or when the catalog / api-keys
 * queries haven't resolved yet.
 *
 * AC-1550: the helper now accepts pairs of ``{ step?, agent }`` so callers
 * can let it apply the AC-1537 effective-model fallback chain
 * (``step.model OR agent.model``) per workflow step. The earlier
 * single-agent shape stays supported via the `agent.model`-only branch
 * for back-compat with callers that pre-resolved the effective model.
 *
 * Inputs:
 *   - `pairs`            For each run_agent step, the (step, agent) pair.
 *                        ``step.model`` takes precedence; falls back to
 *                        ``agent.model``. Pairs where neither has a model
 *                        id are skipped. Pairs where the resolved model
 *                        is not in the catalog are skipped (likely local
 *                        / custom models that don't need BYOK gating).
 *   - `modelToProvider`  Map from LLM-catalog model id to its provider
 *                        identifier.
 *   - `configured`       Set of provider ids the user has configured a
 *                        valid API key for.
 *
 * Returns the sorted unique provider ids that are referenced by the
 * workflow but not configured. Caller renders the banner when this is
 * non-empty.
 */

/** Minimal step shape consumed by the helper. */
export interface MissingKeyStepInput {
  /** Per-step model override (AC-1537). Falls back to agent.model when nullish. */
  model?: string | null;
}

/** Minimal agent shape consumed by the helper. */
export interface MissingKeyAgentInput {
  /** The LLM model id the agent is configured to use (the default for new steps). */
  model?: string | null;
}

/** AC-1550: paired (step, agent) input. */
export interface MissingKeyPair {
  step?: MissingKeyStepInput | null;
  agent?: MissingKeyAgentInput | null;
}

export function computeMissingKeyProviders(
  pairs: ReadonlyArray<MissingKeyPair | MissingKeyAgentInput>,
  modelToProvider: ReadonlyMap<string, string>,
  configured: ReadonlySet<string>,
): string[] {
  const missing = new Set<string>();
  for (const entry of pairs) {
    if (!entry) continue;
    // AC-1550 compatibility shim: the legacy callers passed
    // ``{ model: 'x' }`` directly. Detect that shape by the absence of
    // ``step`` / ``agent`` keys.
    let resolved: string | null | undefined;
    if (
      Object.prototype.hasOwnProperty.call(entry, 'step') ||
      Object.prototype.hasOwnProperty.call(entry, 'agent')
    ) {
      const pair = entry as MissingKeyPair;
      resolved =
        (pair.step && pair.step.model) ||
        (pair.agent && pair.agent.model) ||
        null;
    } else {
      resolved = (entry as MissingKeyAgentInput).model;
    }
    if (!resolved) continue;
    const provider = modelToProvider.get(resolved);
    if (!provider) continue;
    if (!configured.has(provider)) {
      missing.add(provider);
    }
  }
  return Array.from(missing).sort();
}
